import { useEffect, useState } from 'react';
import { api } from '../client/api';
import { useAuthMe } from '../nav/authMe';
import { Button } from './ui';
import { TextPromptModal } from './menu';
import { describeApiError, toast } from './toast';

// =============================================================================
// ActingBanner — BO 운영자가 고객사를 대행해서 보는 중임을 알리는 상단 띠 (2026-09-15 A안).
// "○○ 보기 중 · 읽기 전용 · n분 남음 · [편집 허용] [돌아가기]". 숨기지 않고 항상 보인다.
// 편집 허용 = write 스코프로 재발급(사유 필수, 감사 기록) → 새로고침. 돌아가기 = 세션 종료 → 고객사 상세로.
// 만료되면 서버가 대행을 풀고 /me가 쿠키를 지우므로, 만료 시각에 새로고침해 운영사 화면으로 돌아간다.
// =============================================================================

// 대행 진입 직전에 보던 곳 — 돌아갈 때 이 자리로 되돌린다(2026-09-16 검수 C9).
const RETURN_KEY = 'bricklit.acting.return';

export async function enterTenant(orgId: string, scope: 'read' | 'write', reason?: string): Promise<void> {
  // 스코프 전환(읽기 전용 ↔ 편집 허용)에서는 이미 적어 둔 복귀 지점을 덮지 않는다.
  try {
    if (!sessionStorage.getItem(RETURN_KEY)) {
      sessionStorage.setItem(RETURN_KEY, window.location.hash || `#/h9/tenants/${encodeURIComponent(orgId)}`);
    }
  } catch { /* 저장소가 막힌 환경 — 기본 복귀 지점으로 떨어진다 */ }
  await api.boEnterTenant({ orgId, scope, ...(reason ? { reason } : {}) });
  window.location.hash = '#/media';
  window.location.reload();
}

function returnTarget(orgId: string): string {
  try {
    const saved = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    if (saved && saved.startsWith('#/h9/')) return saved;
  } catch { /* noop */ }
  return `#/h9/tenants/${encodeURIComponent(orgId)}`;
}

function remainingLabel(expiresAt: string, now: number): string {
  const ms = Date.parse(expiresAt) - now;
  if (ms <= 0) return '만료';
  const min = Math.ceil(ms / 60_000);
  return min >= 60 ? `${Math.floor(min / 60)}시간 ${min % 60}분 남음` : `${min}분 남음`;
}

export function ActingBanner(): JSX.Element | null {
  const acting = useAuthMe().me?.acting ?? null;
  const [now, setNow] = useState(() => Date.now());
  const [reasonOpen, setReasonOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!acting) return;
    const tick = window.setInterval(() => setNow(Date.now()), 30_000);
    const untilExpiry = Math.max(1000, Date.parse(acting.expiresAt) - Date.now() + 500);
    const expire = window.setTimeout(() => { toast('고객사 보기 시간이 끝나 운영사 화면으로 돌아갑니다.', 'info'); window.location.reload(); }, untilExpiry);
    return () => { window.clearInterval(tick); window.clearTimeout(expire); };
  }, [acting]);

  if (!acting) return null;
  const readOnly = acting.scope === 'read';

  const leave = async () => {
    setBusy(true);
    try {
      await api.boLeaveTenant();
      window.location.hash = returnTarget(acting.orgId);
      window.location.reload();
    } catch (e) {
      toast(describeApiError(e), 'error');
      setBusy(false);
    }
  };
  const switchScope = async (scope: 'read' | 'write', reason?: string) => {
    setBusy(true);
    try { await enterTenant(acting.orgId, scope, reason); }
    catch (e) { toast(describeApiError(e), 'error'); setBusy(false); }
  };

  return (
    <>
      <div role="status" aria-label="고객사 대행 보기" data-acting-scope={acting.scope}
        className={`flex flex-none flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-1.5 text-[13px] max-md:gap-y-2 max-md:px-3 max-md:py-2.5 ${readOnly ? 'border-accent/40 bg-accent-soft text-text' : 'border-red/40 bg-red/10 text-text'}`}>
        {/* 모바일은 두 줄 — [● 조직명 · 보는 중 ……… 읽기 전용 · 남은 시간] / [버튼 | 버튼](2026-09-23). */}
        <span className="flex min-w-0 items-center gap-2 max-md:flex-1">
          <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${readOnly ? 'bg-accent' : 'bg-red'}`} />
          <span className="min-w-0 truncate font-semibold" title={acting.orgName}>{acting.orgName}</span>
          <span className="flex-none text-text-dim">고객사 화면을 보는 중</span>
        </span>
        <span className="flex flex-none items-center gap-2">
          <span className="tk-tag whitespace-nowrap">{readOnly ? '읽기 전용' : '편집 허용'}</span>
          <span className="whitespace-nowrap text-text-dim tabular-nums" title={`만료 ${new Date(acting.expiresAt).toLocaleString('ko-KR')}`}>{remainingLabel(acting.expiresAt, now)}</span>
        </span>
        <span className="ml-auto flex items-center gap-2 max-md:ml-0 max-md:grid max-md:w-full max-md:grid-cols-2 [&_button]:whitespace-nowrap max-md:[&_button]:!h-10 max-md:[&_button]:!w-full max-md:[&_button]:justify-center max-md:[&_button]:!text-[14px]">
          {readOnly
            ? <Button className="!py-0.5 !text-[12px] max-md:!border-line-strong max-md:!bg-panel" disabled={busy} onClick={() => setReasonOpen(true)} title="고객사 데이터를 바꿀 수 있게 합니다(최대 2시간, 사유가 감사 기록에 남습니다)">편집 허용</Button>
            : <Button className="!py-0.5 !text-[12px] max-md:!border-line-strong max-md:!bg-panel" disabled={busy} onClick={() => void switchScope('read')}>읽기 전용으로</Button>}
          <Button variant="primary" className="!py-0.5 !text-[12px]" disabled={busy} onClick={() => void leave()}>돌아가기</Button>
        </span>
      </div>
      {reasonOpen && (
        <TextPromptModal title="편집 허용" label="사유 (감사 기록에 남습니다)" placeholder="예: 스케줄 오류 문의 대응"
          submitLabel="편집 허용" onClose={() => setReasonOpen(false)}
          onSubmit={async (reason) => { setReasonOpen(false); await switchScope('write', reason); }} />
      )}
    </>
  );
}
